// bulkRevertButton.js - Adds a bulk revert button to account balances pages

// Reference to the MutationObserver instance
let Lux_BulkRevert_observer = null

// Helper function to extract URL parameters at call time (not cached)
function Lux_getBulkRevertUrlParams() {
  const path = window.location.pathname
  const searchParams = new URLSearchParams(window.location.search)
  
  const urlRegex = /^\/account-balances\/([^/]+)\/account\/([^/]+)/
  const match = path.match(urlRegex)
  
  if (!match) {
    return null
  }

  return {
    propertyHrId: match[1],
    accountCode: decodeURIComponent(match[2]),
    startDate: searchParams.get('startDate'),
    endDate: searchParams.get('endDate'),
  }
}

// Helper to set button loading state
function Lux_setBulkRevertButtonLoading(button, isLoading) {
  if (isLoading) {
    button.disabled = true
    button.dataset.originalText = button.textContent
    button.innerHTML = `
      <span style="display: inline-flex; align-items: center; gap: 6px;">
        <span class="lux-spinner" style="
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 2px solid rgba(0, 0, 0, 0.2);
          border-top-color: #1890ff;
          border-radius: 50%;
          animation: lux-spin 0.8s linear infinite;
        "></span>
        Wird verarbeitet...
      </span>
    `
    // Inject keyframes if not already present
    if (!document.getElementById('lux-spinner-styles')) {
      const style = document.createElement('style')
      style.id = 'lux-spinner-styles'
      style.textContent = `
        @keyframes lux-spin {
          to { transform: rotate(360deg); }
        }
      `
      document.head.appendChild(style)
    }
  } else {
    button.disabled = false
    button.textContent = button.dataset.originalText || 'Massen-Storno'
  }
}

function Lux_initializeBulkRevertButton() {
  const anchorSelector = '.header-component'
  const buttonId = 'lux-bulk-revert-btn'

  // Disconnect previous observer if exists
  if (Lux_BulkRevert_observer) {
    Lux_BulkRevert_observer.disconnect()
    Lux_BulkRevert_observer = null
  }

  // Check if we're on the right page (basic validation at init time)
  const params = Lux_getBulkRevertUrlParams()
  if (!params) {
    return
  }

  // Permission check: only tech or accounting groups can use this feature
  if (typeof isInRole !== 'function' || (!isInRole('tech') && !isInRole('accounting'))) {
    return
  }

  // Function to create and inject the button
  const injectButton = (anchorElement) => {
    if (document.getElementById(buttonId)) {
      return
    }

    const bulkRevertButton = document.createElement('button')
    bulkRevertButton.type = 'button'
    bulkRevertButton.classList = 'ant-btn ant-btn-text Button'
    bulkRevertButton.id = buttonId
    bulkRevertButton.textContent = 'Massen-Storno'
    
    // Apply custom styles
    bulkRevertButton.style.backgroundColor = 'rgba(0, 0, 0, 0.08)'
    bulkRevertButton.addEventListener('mouseenter', () => {
      if (!bulkRevertButton.disabled) {
        bulkRevertButton.style.backgroundColor = 'rgba(0, 0, 0, 0.16)'
      }
    })
    bulkRevertButton.addEventListener('mouseleave', () => {
      if (!bulkRevertButton.disabled) {
        bulkRevertButton.style.backgroundColor = 'rgba(0, 0, 0, 0.08)'
      }
    })

    // Insert the button as the first child of .header-component
    anchorElement.insertBefore(bulkRevertButton, anchorElement.firstChild)

    bulkRevertButton.addEventListener('click', async () => {
      // Extract URL parameters at click time to respect SPA navigation
      const currentParams = Lux_getBulkRevertUrlParams()
      if (!currentParams) {
        alert('Parameter konnten nicht aus der aktuellen URL extrahiert werden')
        return
      }

      const { propertyHrId, accountCode, startDate, endDate } = currentParams

      if (!propertyHrId || !accountCode || !startDate || !endDate) {
        alert('Erforderliche URL-Parameter fehlen (startDate oder endDate)')
        return
      }

      Lux_setBulkRevertButtonLoading(bulkRevertButton, true)

      try {
        // Get auth token from localStorage
        const authData = localStorage.getItem('auth')
        if (!authData) {
          throw new Error('Authentifizierungsdaten nicht gefunden')
        }
        const token = JSON.parse(authData).token
        if (!token) {
          throw new Error('Authentifizierungstoken nicht gefunden')
        }

        // Build the API URL
        const apiUrl = `/services/pmp-accounting/api/v1/postings/accounts?sort=postDate,DESC&accountCode=${encodeURIComponent(accountCode)}&propertyHrId=${encodeURIComponent(propertyHrId)}&includeCorrectional=false&fromDate=${startDate}&toDate=${endDate}`
        
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        })
        if (!response.ok) {
          throw new Error(`HTTP-Fehler: ${response.status}`)
        }
        
        const data = await response.json()
        const postingItems = data.postingItemDetails || []
        
        // Separate items with and without exchangeId
        const itemsWithExchangeId = postingItems.filter(item => item.exchangeId !== undefined && item.exchangeId !== null)
        const itemsWithoutExchangeId = postingItems.filter(item => item.exchangeId === undefined || item.exchangeId === null)
        
        const totalCount = postingItems.length
        const revertCount = itemsWithExchangeId.length
        const correctCount = itemsWithoutExchangeId.length
        
        // Show confirmation dialog
        const confirmMessage = `Möchten Sie wirklich ${totalCount} Buchung(en) verarbeiten?\n\n` +
          `- ${revertCount} werden STORNIERT (haben exchangeId)\n` +
          `- ${correctCount} werden KORRIGIERT (keine exchangeId)`
        
        if (!window.confirm(confirmMessage)) {
          Lux_setBulkRevertButtonLoading(bulkRevertButton, false)
          return
        }
        
        // Process items with exchangeId - call revert endpoint
        for (const item of itemsWithExchangeId) {
          const revertUrl = `/services/pmp-accounting/api/v1/accounting/exchanges/${item.exchangeId}/revert`
          
          const revertResponse = await fetch(revertUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          })
          
          if (!revertResponse.ok) {
            console.error(`Lux Inject: Failed to revert exchange ${item.exchangeId}:`, revertResponse.status)
          }
        }
        
        // Process items without exchangeId - call corrections endpoint
        for (const item of itemsWithoutExchangeId) {
          const correctUrl = `/services/pmp-accounting/api/v1/postings/${item.postingId}/corrections`
          
          const correctResponse = await fetch(correctUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          })
          
          if (!correctResponse.ok) {
            console.error(`Lux Inject: Failed to correct posting ${item.postingId}:`, correctResponse.status)
          }
        }
        
        Lux_setBulkRevertButtonLoading(bulkRevertButton, false)
        
        // Notify user of completion
        alert('Massen-Storno abgeschlossen! Bitte aktualisieren Sie die Seite, um die aktualisierten Daten zu sehen.')
        
      } catch (error) {
        console.error('Lux Inject: Error during bulk revert:', error)
        Lux_setBulkRevertButtonLoading(bulkRevertButton, false)
        alert('Fehler beim Massen-Storno: ' + error.message)
      }
    })
  }

  // Check if element already exists
  const anchorElement = document.querySelector(anchorSelector)
  if (anchorElement) {
    injectButton(anchorElement)
    return
  }

  // Set up MutationObserver to wait for the header-component
  Lux_BulkRevert_observer = new MutationObserver((mutationsList, observer) => {
    const anchorElement = document.querySelector(anchorSelector)
    if (anchorElement) {
      observer.disconnect()
      Lux_BulkRevert_observer = null
      injectButton(anchorElement)
    }
  })

  Lux_BulkRevert_observer.observe(document.body, {
    childList: true,
    subtree: true,
  })
}
